import React, { useMemo, useState } from "react";
import { Spinner, Table } from "react-bootstrap";




// ===== Utilities =====
const getValueByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
};

const defaultCell = (v) => (v == null ? "" : String(v));

const isHeaderGroup = (colOrGroup) => Array.isArray(colOrGroup?.columns);

// Build header rows matrix and leaves
const buildHeaderLayout = (cols) => {
  const depth = getDepth(cols);
  const rows = Array.from({ length: depth }, () => []);
  const leaves = [];

  const walk = (nodes, level = 0) => {
    nodes.forEach((node) => {
      if (isHeaderGroup(node)) {
        const leafCount = countLeaves(node);
        rows[level].push({
          id: `group-${node.header}-${level}-${rows[level].length}`,
          header: node.header,
          colSpan: leafCount,
          rowSpan: 1,
          isGroup: true,
        });
        walk(node.columns, level + 1);
      } else {
        const rowSpan = depth - level;
        rows[level].push({
          id: node.id,
          header: node.header,
          colSpan: 1,
          rowSpan,
          isGroup: false,
          column: node,
        });
        leaves.push(node);
      }
    });
  };

  walk(cols, 0);
  return { headerRows: rows, leaves };
};

const getDepth = (nodes) => {
  let max = 0;
  const walk = (arr, level) => {
    max = Math.max(max, level + 1);
    arr.forEach((n) => (isHeaderGroup(n) ? walk(n.columns, level + 1) : null));
  };
  walk(nodes, 0);
  return max;
};

const countLeaves = (node) => {
  if (!isHeaderGroup(node)) return 1;
  return node.columns.reduce((sum, c) => sum + countLeaves(c), 0);
};

// ===== Sorting helpers =====
const parseAsNumber = (v) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    // remove commas and currency symbols
    const n = Number(v.replace(/[\s,₹$€£%]/g, ""));
    return isNaN(n) ? NaN : n;
  }
  return NaN;
};

const parseAsDate = (v) => {
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v);
  if (typeof v === "string") {
    // Try: ISO, dd/mm/yyyy, dd-mm-yyyy, mm/dd/yyyy
    const s = v.trim();
    let d = new Date(s);
    if (!isNaN(d)) return d;

    const ddmmyyyy = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
    if (ddmmyyyy) {
      let [_, d1, m1, y1] = ddmmyyyy;
      if (y1.length === 2) y1 = (Number(y1) + 2000).toString();
      const dt = new Date(Number(y1), Number(m1) - 1, Number(d1));
      if (!isNaN(dt)) return dt;
    }
    return new Date(NaN);
  }
  return new Date(NaN);
};

const compareValues = (a, b, type = "string") => {
  if (type === "number") {
    const na = parseAsNumber(a);
    const nb = parseAsNumber(b);
    if (isNaN(na) && isNaN(nb)) return 0;
    if (isNaN(na)) return 1; // NaN last
    if (isNaN(nb)) return -1;
    return na - nb;
  }
  if (type === "date") {
    const da = parseAsDate(a)?.getTime();
    const db = parseAsDate(b)?.getTime();
    if (isNaN(da) && isNaN(db)) return 0;
    if (isNaN(da)) return 1;
    if (isNaN(db)) return -1;
    return da - db;
  }
  // string default
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true, sensitivity: "base" });
};

// ===== Table Component =====
const CustomTable=({ columns, data, initialSort,loading ,context})=> {
  const { headerRows, leaves } = useMemo(() => buildHeaderLayout(columns), [columns]);

  const [sort, setSort] = useState(() => {
    if (initialSort && initialSort.id) {
      return { id: initialSort.id, direction: initialSort.direction === "desc" ? "desc" : "asc" };
    }
    // default: first leaf sortable column
    const firstSortable = (leaves || []).find((c) => c.sortable !== false);
    return firstSortable ? { id: firstSortable.id, direction: "asc" } : { id: null, direction: "asc" };
  });

  const sorted = useMemo(() => {
    if (!sort?.id) return data || [];
    const col = leaves.find((c) => c.id === sort.id);
    if (!col) return data || [];

    const copy = [...(data || [])];
    const dir = sort.direction === "desc" ? -1 : 1;

    copy.sort((ra, rb) => {
      const va = col.accessor ? getValueByPath(ra, col.accessor) : ra[col.id];
      const vb = col.accessor ? getValueByPath(rb, col.accessor) : rb[col.id];
      const cmp = compareValues(va, vb, col.type || "string");
      return cmp * dir;
    });
    return copy;
  }, [data, leaves, sort]);

  const onHeaderClick = (cell) => {
    if (cell.isGroup) return; // groups aren't sortable
    const col = cell.column;
    if (col?.sortable === false) return;
    setSort((prev) =>
      prev?.id === col.id ? { id: col.id, direction: prev.direction === "asc" ? "desc" : "asc" } : { id: col.id, direction: "asc" }
    );
  };

  return (
    <div className="table-cover">
 {loading && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ background: "rgba(255,255,255,0.6)", zIndex: 2 }}
                  >
                    <Spinner animation="border" />
                  </div>
                )}

      <Table style={styles.table} hover bordered className="position-relative mb-0">
        <thead>
          {headerRows.map((row, rIdx) => (
            <tr key={rIdx}>
              {row.map((cell) => {
                const isActive = !cell.isGroup && sort?.id === cell.column?.id;
                const isSortable = !cell.isGroup && cell.column?.sortable !== false;
                return (
                  <th
                    key={cell.id}
                    colSpan={cell.colSpan}
                    rowSpan={cell.rowSpan}
                    onClick={() => (isSortable ? onHeaderClick(cell) : null)}
                    style={{
                      ...styles.th,
                      cursor: isSortable ? "pointer" : "default",
                      textAlign: cell.column?.align || "left",
                      minWidth: cell.column?.minWidth,
                      width: cell.column?.width,
                      maxWidth: cell.column?.maxWidth,
                      background: isActive ? "#f3f4f6" : undefined,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                    aria-sort={isActive ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <div style={styles.thContent}>
                      <span>{cell.header}</span>
                      {!cell.isGroup && (
                        <span style={styles.sortIcon}>
                          {sort?.id === cell.column?.id ? (sort.direction === "asc" ? "▲" : "▼") : cell.column?.sortable === false ? "" : "↕"}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>


          {sorted.map((row, i) => (
            <tr key={i}>
              {leaves.map((col) => {
                const raw = col.accessor ? getValueByPath(row, col.accessor) : row[col.id];
                return (
                  <td key={col.id} style={{ ...styles.td, textAlign: col.align || "left" }}>
                    {col.cell ? col.cell(raw, row,context) : defaultCell(raw)}
                  </td>
                );
              })}
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={leaves.length} style={{ ...styles.td, textAlign: "center", color: "#6b7280" }}>
                No data
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

// ===== Inline styles (feel free to swap with Tailwind/Bootstrap) =====
const styles = {
  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    fontSize: 14,
  },
  th: {
    position: "relative",
    background: "#fafafa",
    color: "#111827",
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    borderRight: "1px solid #f3f4f6",
    whiteSpace: "nowrap",
  },
  thContent: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  sortIcon: { marginLeft: 6, fontSize: 10, opacity: 0.7 },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #f3f4f6",
    borderRight: "1px solid #f9fafb",
    background: "#fff",
  },
};



export default CustomTable;

